import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button, Card, Badge, Alert } from '../components/design-system';
import { ArrowLeft, Sparkles, ExternalLink, X, Trash2 } from 'lucide-react';

export const ClusterDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isEditor, isAdmin } = useAuth();
  const [cluster, setCluster] = useState(null);
  const [items, setItems] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { loadCluster(); }, [id]);

  const loadCluster = async () => {
    try {
      const res = await api.getCluster(id);
      setCluster(res.cluster);
      setItems(res.items || []);
      setArticles(res.articles || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleGenerate = async () => {
    setGenerating(true); setError('');
    try {
      const res = await api.generateFromCluster(id);
      if (res.article) navigate(`/articles/${res.article.id}`);
    } catch (err) { setError(err.message); }
    finally { setGenerating(false); }
  };

  const handleRemoveItem = async (itemId) => {
    setError(''); setSuccess('');
    try {
      const res = await api.removeItemFromCluster(id, itemId);
      setSuccess(res.message);
      if (res.clusterDeleted) {
        setTimeout(() => navigate('/clusters'), 1500);
      } else {
        loadCluster();
      }
    } catch (err) { setError(err.message); }
  };

  const handleDeleteCluster = async () => {
    if (!confirm('Delete this cluster? Items will be released for re-clustering.')) return;
    setError('');
    try {
      const res = await api.deleteCluster(id);
      setSuccess(res.message);
      setTimeout(() => navigate('/clusters'), 1500);
    } catch (err) { setError(err.message); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" /></div>;
  if (!cluster) return <div className="text-center py-20 text-gray-500">Cluster not found</div>;

  const topics = safeParseArray(cluster.topics);
  const entities = safeParseArray(cluster.entities);

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/clusters')} className="p-2 rounded-lg hover:bg-gray-100">
          <ArrowLeft className="h-4 w-4 text-gray-500" />
        </button>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">{cluster.title}</h2>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="primary">{cluster.source_count} sources</Badge>
            <Badge variant="info">{cluster.item_count} items</Badge>
            <Badge variant={cluster.status === 'active' ? 'success' : 'default'}>{cluster.status}</Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {items.length >= 2 && (
            <Button icon={Sparkles} loading={generating} onClick={handleGenerate}>Generate Article</Button>
          )}
          {isAdmin && (
            <Button icon={Trash2} variant="danger" size="sm" onClick={handleDeleteCluster}>Delete</Button>
          )}
        </div>
      </div>

      {error && <Alert type="error" onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Cluster Info */}
      <Card className="!p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div><div className="text-xs text-gray-500">Importance</div><div className="text-lg font-bold text-primary-600">{Math.round((cluster.importance_score || 0) * 100)}%</div></div>
          <div><div className="text-xs text-gray-500">Sources</div><div className="text-lg font-bold">{cluster.source_count}</div></div>
          <div><div className="text-xs text-gray-500">Items</div><div className="text-lg font-bold">{cluster.item_count}</div></div>
          <div><div className="text-xs text-gray-500">First Seen</div><div className="text-sm font-medium">{new Date(cluster.first_seen_at).toLocaleDateString()}</div></div>
        </div>
        {(topics.length > 0 || entities.length > 0) && (
          <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap gap-1">
            {topics.map((t, i) => <Badge key={`t-${i}`} variant="primary">{t}</Badge>)}
            {entities.map((e, i) => <Badge key={`e-${i}`} variant="info">{e}</Badge>)}
          </div>
        )}
      </Card>

      {/* Generated Articles */}
      {articles.length > 0 && (
        <Card>
          <h3 className="text-base font-semibold text-gray-900 mb-3">Generated Articles</h3>
          <div className="space-y-2">
            {articles.map(a => (
              <div key={a.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/articles/${a.id}`)}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{a.headline}</div>
                  <div className="text-xs text-gray-400">{new Date(a.created_at).toLocaleString()}</div>
                </div>
                <Badge variant={a.editorial_status === 'published' ? 'success' : a.editorial_status === 'review' ? 'warning' : 'default'}>
                  {a.editorial_status}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Source Items */}
      <Card>
        <h3 className="text-base font-semibold text-gray-900 mb-3">Source Items ({items.length})</h3>
        <div className="space-y-2">
          {items.map(item => {
            let displaySource = item.source_name || 'Unknown';
            if (item.url) { try { displaySource = new URL(item.url).hostname.replace(/^www\./, ''); } catch {} }
            return (
              <div key={item.id} className="p-3 border border-gray-100 rounded-lg group">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900">{item.title}</div>
                    {item.summary && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.summary}</p>}
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="default">{displaySource}</Badge>
                      {item.published_at && <span className="text-xs text-gray-400">{new Date(item.published_at).toLocaleString()}</span>}
                      {item.importance_score > 0 && <span className="text-xs text-primary-600">{Math.round(item.importance_score * 100)}%</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {item.url && (
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-gray-100 rounded" onClick={e => e.stopPropagation()}>
                        <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                      </a>
                    )}
                    {isEditor && (
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        className="p-1.5 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove from cluster"
                      >
                        <X className="h-3.5 w-3.5 text-red-400" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};

function safeParseArray(str) { try { return JSON.parse(str || '[]'); } catch { return []; } }
